import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Disclosure, Box, Table, TableBody, TableRow, TableHeaderCell, TableCell, Text, TextLinkButton, Stack, Columns, Column, FieldLabel, RadioGroup, RadioItem, Toggle, Drawer, Divider, Badge, Heading } from "braid-design-system";
import { createContext, useMemo, useState, useCallback, useContext, useEffect, useRef } from "react";
import { useMelwaysInfo } from "@seek/melways-react";
import * as Apollo from "@apollo/client";
import { gql } from "@apollo/client";
import UAParser from "ua-parser-js";
import { FEATURE_FLAGS_CONTAINER_SELECTOR } from "./FeatureFlagsController.css.mjs";
const defaultOptions = {};
var ExperimentDeviceType = /* @__PURE__ */ ((ExperimentDeviceType2) => {
  ExperimentDeviceType2["PHONE"] = "PHONE";
  ExperimentDeviceType2["TABLET"] = "TABLET";
  return ExperimentDeviceType2;
})(ExperimentDeviceType || {});
var Platform = /* @__PURE__ */ ((Platform2) => {
  Platform2["ANDROID"] = "ANDROID";
  Platform2["IOS"] = "IOS";
  Platform2["WEB"] = "WEB";
  return Platform2;
})(Platform || {});
const FeatureFlagCacheValueFragmentDoc = gql`
    fragment FeatureFlagCacheValue on FeatureFlagEvaluation {
  value
}
    `;
const FeatureFlagsDocument = gql`
    query FeatureFlags($flags: [String!]!, $visitorContext: VisitorContextInput, $experienceContext: ExperienceContextInput, $applicationContext: ApplicationContextInput, $deviceContext: DeviceContextInput) {
  featureFlags(
    flags: $flags
    visitorContext: $visitorContext
    experienceContext: $experienceContext
    applicationContext: $applicationContext
    deviceContext: $deviceContext
  ) {
    id
    key
    value
    variation
    reason
    error
  }
}
    `;
function useFeatureFlagsQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery(FeatureFlagsDocument, options);
}
const getDefaultUserAgent = () => {
  var _a;
  return typeof window !== "undefined" ? (_a = window.navigator) == null ? void 0 : _a.userAgent : "";
};
const isGoogleBot = (ua) => ua.toLowerCase().includes("google");
const isDatadogSyntheticTest = (ua) => /Datadog|Synthetics/.test(ua);
const checkIsBotVisit = (userAgent = getDefaultUserAgent()) => isGoogleBot(userAgent) || isDatadogSyntheticTest(userAgent);
const botVisitDefaultValue = (flags) => flags.map((flag) => ({
  id: flag,
  key: flag,
  value: false,
  variation: "0",
  reason: "BOT_VISIT"
}));
const toFeatureFlagEvaluation = (flag) => ({
  id: flag.id,
  key: flag.key,
  value: flag.value,
  variation: flag.variation || "",
  reason: flag.reason
});
const mapResultToContextData = (data, features) => {
  var _a;
  return ((_a = data == null ? void 0 : data.featureFlags) == null ? void 0 : _a.filter((flag) => features.includes(flag.key)).map(toFeatureFlagEvaluation)) ?? [];
};
const getDeviceInfo = (userAgent = getDefaultUserAgent()) => {
  const parser = new UAParser(userAgent || "");
  const device = parser.getDevice();
  const os = parser.getOS();
  let type = null;
  const userAgentDeviceType = device == null ? void 0 : device.type;
  switch (userAgentDeviceType == null ? void 0 : userAgentDeviceType.toLowerCase()) {
    case "mobile":
      type = ExperimentDeviceType.PHONE;
      break;
    case "tablet":
      type = ExperimentDeviceType.TABLET;
      break;
    default:
      type = null;
  }
  return {
    manufacturer: (device == null ? void 0 : device.vendor) || null,
    model: (device == null ? void 0 : device.model) || null,
    os: (os == null ? void 0 : os.name) && (os == null ? void 0 : os.version) && {
      name: os.name,
      version: os.version
    } || null,
    type
  };
};
const enrolmentReasonList = [
  "FALLTHROUGH",
  "TARGET_MATCH",
  "RULE_MATCH"
];
const isEnrolledInExperiment = ({ reason }) => enrolmentReasonList.includes(reason);
const FEATURE_COOKIE_PREFIX = "feature_";
const extractFeatureFlagsCookies = (cookies) => Object.entries(cookies).reduce(
  (acc, [key, value]) => {
    if (key.startsWith(FEATURE_COOKIE_PREFIX)) {
      const featureFlag = key.replace(FEATURE_COOKIE_PREFIX, "");
      if (value !== void 0) {
        acc[featureFlag] = value;
      }
    }
    return acc;
  },
  {}
);
const setFeatureFlagCookie = (key, value) => {
  setCookie(`${FEATURE_COOKIE_PREFIX}${key}`, value);
};
const setCookie = (key, value) => {
  if (typeof document === "undefined") {
    return;
  }
  document.cookie = `${key}=${value}`;
};
const resetAllFeatureFlagCookies = () => {
  Object.keys(parseDocumentFeatureFlagCookies()).forEach(
    (key) => resetFeatureCookie(key)
  );
};
const resetFeatureCookie = (key) => {
  document.cookie = `${FEATURE_COOKIE_PREFIX}${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};
const parseDocumentCookies = () => {
  if (typeof document === "undefined") {
    return {};
  }
  return document.cookie.split(";").reduce((acc, cookie) => {
    const [key, value] = cookie.split("=");
    acc[key.trim()] = value;
    return acc;
  }, {});
};
const parseDocumentFeatureFlagCookies = () => extractFeatureFlagsCookies(parseDocumentCookies());
const parseOverride = ({ type }, value) => {
  if (value === void 0) {
    return;
  }
  switch (type) {
    case "boolean":
      return value === "true";
    case "number":
      return !Number.isNaN(Number(value)) ? Number(value) : void 0;
    case "string":
    case "json":
      return value;
    default:
      return;
  }
};
const processOverride = ({
  arr,
  override,
  value
}) => {
  var _a, _b;
  const val = parseOverride(override, value);
  const evaluationOverride = {
    options: override,
    value: void 0
  };
  if (val !== void 0) {
    if (override.type === "json") {
      evaluationOverride.variation = val.toString();
      evaluationOverride.value = (_a = override.variations[Number(val)]) == null ? void 0 : _a.value;
    } else {
      evaluationOverride.value = val;
      if (typeof val === "boolean") {
        evaluationOverride.variation = val ? "0" : "1";
      } else {
        evaluationOverride.variation = ((_b = override.variations) == null ? void 0 : _b.findIndex((v) => v === val).toString()) ?? "0";
      }
    }
  }
  arr.push(evaluationOverride);
  return arr;
};
const getFeatureOverrides = ({
  experimentsConfig,
  featureFlagsConfig,
  overrideSource,
  shouldEnableOverride
}) => {
  if (!shouldEnableOverride) {
    return [];
  }
  const allOverrides = overrideSource || parseDocumentFeatureFlagCookies();
  const featuresOverrides = featureFlagsConfig.reduce(
    (acc, override) => processOverride({
      arr: acc,
      override,
      value: allOverrides[override.key]
    }),
    []
  );
  const experimentsOverrides = experimentsConfig.reduce(
    (acc, override) => processOverride({
      arr: acc,
      override,
      value: allOverrides[override.key]
    }),
    []
  );
  return [...featuresOverrides, ...experimentsOverrides];
};
const overrideEvaluation = ({
  feature,
  overrides
}) => {
  if (!feature || !(overrides == null ? void 0 : overrides.length)) {
    return feature;
  }
  const override = overrides.find((o) => o.options.key === feature.key);
  if (override) {
    return {
      ...feature,
      value: override.value ?? feature.value,
      variation: override.variation ?? feature.variation
    };
  }
  return feature;
};
const resetAll = ({
  overrides,
  overrideCallback
}) => {
  resetAllFeatureFlagCookies();
  return overrides == null ? void 0 : overrides.map((override) => {
    overrideCallback == null ? void 0 : overrideCallback(override.options.key);
    return {
      ...override,
      value: void 0,
      variation: void 0
    };
  });
};
const syncOverrides = ({
  key,
  overrides,
  value,
  overrideCallback
}) => overrides == null ? void 0 : overrides.map((override) => {
  if (override.options.key === key) {
    if (value === void 0) {
      resetFeatureCookie(key);
      overrideCallback == null ? void 0 : overrideCallback(key);
      return {
        ...override,
        value,
        variation: void 0
      };
    }
    if (override.options.type === "boolean") {
      setFeatureFlagCookie(key, value.toString());
      overrideCallback == null ? void 0 : overrideCallback(key, value.toString());
      return {
        ...override,
        value,
        variation: Number(!value).toString()
      };
    }
    if (override.options.type === "json") {
      const variations2 = override.options.variations;
      const currentValue2 = override.value;
      const currentVariation2 = override.variation;
      const potentialValue = variations2[Number(value)];
      const isValidValue = potentialValue !== void 0;
      const newValue2 = isValidValue ? potentialValue.value : currentValue2;
      const newVariation2 = isValidValue ? value.toString() : currentVariation2;
      if (newVariation2) {
        setFeatureFlagCookie(key, newVariation2);
        overrideCallback == null ? void 0 : overrideCallback(key, newVariation2);
      } else {
        resetFeatureCookie(key);
        overrideCallback == null ? void 0 : overrideCallback(key);
      }
      return {
        ...override,
        value: newValue2,
        variation: newVariation2
      };
    }
    const variations = override.options.variations;
    const currentValue = override.value;
    const currentVariation = override.variation;
    const potentialVariation = variations == null ? void 0 : variations.findIndex(
      (v) => v.toString() === (value == null ? void 0 : value.toString())
    );
    const isValidVariation = potentialVariation !== void 0 && potentialVariation >= 0;
    const newValue = isValidVariation ? value : currentValue;
    const newVariation = isValidVariation ? potentialVariation.toString() : currentVariation;
    setFeatureFlagCookie(key, (newValue ?? value).toString());
    overrideCallback == null ? void 0 : overrideCallback(key, (newValue ?? value).toString());
    return {
      ...override,
      value: newValue,
      variation: newVariation
    };
  }
  return override;
});
const FeatureFlags = createContext({});
const FeatureFlagProvider = ({
  featureFlagsConfig,
  experimentsConfig,
  customContext,
  debug = false,
  userAgent,
  solId,
  children,
  overrideSource,
  shouldEnableOverride,
  overrideCallback
}) => {
  var _a;
  const { language, locale, country, brand } = useMelwaysInfo();
  const contextCountry = country == null ? void 0 : country.toUpperCase();
  const experimentFlags = useMemo(
    () => experimentsConfig.map((experiment) => experiment.key),
    [experimentsConfig]
  );
  const featureFlags = useMemo(
    () => featureFlagsConfig.map((flag) => flag.key),
    [featureFlagsConfig]
  );
  const flagsList = useMemo(
    () => [...featureFlags, ...experimentFlags],
    [featureFlags, experimentFlags]
  );
  const [overrides, setOverrides] = useState(
    getFeatureOverrides({
      experimentsConfig,
      featureFlagsConfig,
      overrideSource,
      shouldEnableOverride
    })
  );
  const hasOverrides = useMemo(
    () => overrides == null ? void 0 : overrides.some((o) => o.value !== void 0),
    [overrides]
  );
  const overrideFlag = useCallback(
    (key, value) => setOverrides(
      (o) => syncOverrides({
        key,
        value,
        overrides: o,
        overrideCallback
      })
    ),
    [overrideCallback]
  );
  const resetAllOverrides = useCallback(() => {
    setOverrides((o) => resetAll({ overrides: o, overrideCallback }));
  }, [overrideCallback]);
  const isBotVisit = useMemo(() => checkIsBotVisit(userAgent), [userAgent]);
  const deviceInfo = useMemo(() => getDeviceInfo(userAgent), [userAgent]);
  const shouldSkip = useMemo(
    // Even though solId is required, it might be an empty string
    // Sending contexts with empty keys results in errors in the backend
    () => !solId || isBotVisit || flagsList.length === 0,
    [flagsList.length, isBotVisit, solId]
  );
  const { data, loading, error } = useFeatureFlagsQuery({
    variables: {
      flags: flagsList,
      visitorContext: { key: solId },
      deviceContext: {
        key: solId,
        locale,
        // TODO: This country should follow the user device country, and not
        // the current site country.
        country: contextCountry,
        ...deviceInfo
      },
      experienceContext: {
        key: solId,
        brand,
        country: contextCountry,
        language
      },
      ...customContext,
      applicationContext: {
        key: solId,
        ...customContext == null ? void 0 : customContext.applicationContext,
        // Always forcing the platform to be Web for web applications
        channel: Platform.WEB
      }
    },
    skip: shouldSkip
  });
  const featureFlagsContext = useMemo(() => {
    var _a2;
    const experiments = isBotVisit ? botVisitDefaultValue(experimentFlags) : data && mapResultToContextData(data, experimentFlags);
    return {
      applicationName: ((_a2 = customContext == null ? void 0 : customContext.applicationContext) == null ? void 0 : _a2.namespace) || "",
      isExperimentsReady: !loading,
      featureFlagsConfig,
      experimentsConfig,
      ...data && {
        featureFlags: mapResultToContextData(data, featureFlags),
        experiments,
        enrolledExperiments: experiments ? experiments.filter(
          (experiment) => isEnrolledInExperiment(experiment)
        ) : []
      },
      overrides,
      hasOverrides,
      overrideFlag,
      resetAllOverrides
    };
  }, [
    isBotVisit,
    experimentFlags,
    data,
    (_a = customContext == null ? void 0 : customContext.applicationContext) == null ? void 0 : _a.namespace,
    loading,
    featureFlags,
    experimentsConfig,
    featureFlagsConfig,
    overrides,
    hasOverrides,
    overrideFlag,
    resetAllOverrides
  ]);
  if (debug) {
    console.log("FeatureFlagProvider", featureFlagsContext);
    if (error) {
      console.error("FeatureFlagProvider error", error);
    }
  }
  return /* @__PURE__ */ jsx(FeatureFlags.Provider, { value: featureFlagsContext, children });
};
const useFeatureFlagContext = () => {
  const featureFlagsContext = useContext(FeatureFlags);
  return featureFlagsContext;
};
const useOverriddenFeatures = ({
  features
}) => {
  const { overrides, hasOverrides } = useFeatureFlagContext();
  return useMemo(() => {
    if (!hasOverrides || !overrides) {
      return features;
    }
    if (features.length) {
      return features.map(
        (feature) => overrideEvaluation({
          feature,
          overrides
        })
      ).filter((feature) => feature !== void 0);
    }
    return overrides.map(
      ({ options: { key }, value, variation }) => value !== void 0 && variation !== void 0 ? { id: key, key, reason: "OVERRIDE", value, variation } : void 0
    ).filter((feature) => feature !== void 0);
  }, [features, hasOverrides, overrides]);
};
const SEQUENCE = [
  "QXJyb3dMZWZ0",
  "QXJyb3dSaWdodA==",
  "QXJyb3dMZWZ0",
  "QXJyb3dSaWdodA==",
  "cw==",
  "ZQ==",
  "ZQ==",
  "aw=="
];
const toBase64 = (str) => {
  const utf8Bytes = new TextEncoder().encode(str);
  const binaryString = String.fromCharCode(...utf8Bytes);
  return window == null ? void 0 : window.btoa(binaryString);
};
const isSequenceInputted = ({
  userInput,
  sequence
}) => userInput.map(toBase64).join("").includes(sequence.join(""));
const useToggleFeatureOverride = ({
  onSuccess,
  keySequence = SEQUENCE,
  tapCount = 10,
  timeLimit = 4e3,
  isHidden = true
}) => {
  const [, setKeyInput] = useState([]);
  const [tapCounter, setTapCounter] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const handleKeyPress = useCallback(
    (event) => {
      if (!isHidden) {
        return;
      }
      setKeyInput((prev) => {
        const newSequence = [...prev, event.key].slice(-keySequence.length);
        if (isSequenceInputted({ userInput: newSequence, sequence: keySequence })) {
          onSuccess();
          return [];
        }
        return newSequence;
      });
    },
    [isHidden, keySequence, onSuccess]
  );
  const handleTap = useCallback(() => {
    if (!isHidden) {
      return;
    }
    const now = Date.now();
    if (!startTime || now - startTime > timeLimit) {
      setTapCounter(1);
      setStartTime(now);
    } else {
      setTapCounter((prev) => prev + 1);
    }
    if (tapCounter + 1 >= tapCount) {
      onSuccess();
      setTapCounter(0);
      setStartTime(null);
    }
  }, [isHidden, startTime, timeLimit, tapCounter, tapCount, onSuccess]);
  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    window.addEventListener("touchstart", handleTap, { passive: true });
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
      window.removeEventListener("touchstart", handleTap);
    };
  }, [
    keySequence,
    tapCount,
    timeLimit,
    tapCounter,
    startTime,
    onSuccess,
    handleKeyPress,
    handleTap
  ]);
};
const isExperimentConfig = (config) => config.hasOwnProperty("trackingNumber");
const ShelfElement = ({ label, value }) => value !== void 0 && /* @__PURE__ */ jsxs(TableRow, { children: [
  /* @__PURE__ */ jsx(TableHeaderCell, { width: "content", children: label }),
  /* @__PURE__ */ jsx(TableCell, { wrap: true, children: value })
] }, label);
const FeatureShelf = (props) => {
  const shouldShow = Object.values(props).some((value) => value !== void 0);
  const { trackingNumber, variation, reason, originalValue } = props;
  return shouldShow && /* @__PURE__ */ jsx(Disclosure, { id: "", expandLabel: "Show More", collapseLabel: "Hide", children: /* @__PURE__ */ jsx(Box, { maxWidth: "content", children: /* @__PURE__ */ jsx(Table, { alignY: "center", label: "Feature Flag Details", children: /* @__PURE__ */ jsxs(TableBody, { children: [
    /* @__PURE__ */ jsx(
      ShelfElement,
      {
        label: "Original Value",
        value: originalValue == null ? void 0 : originalValue.toString()
      }
    ),
    /* @__PURE__ */ jsx(ShelfElement, { label: "Reason", value: reason }),
    /* @__PURE__ */ jsx(ShelfElement, { label: "Variation", value: variation }),
    /* @__PURE__ */ jsx(
      ShelfElement,
      {
        label: "Tracking Number",
        value: trackingNumber == null ? void 0 : trackingNumber.toString()
      }
    )
  ] }) }) }) });
};
const noop = () => {
};
const ResetButton = ({ onClick, feature, disabled }) => {
  const title = feature ? "Reset" : "Reset all";
  const size = feature ? "small" : "standard";
  const tone = feature ? "caution" : "critical";
  return /* @__PURE__ */ jsx(Text, { size, tone: disabled ? "neutral" : tone, children: /* @__PURE__ */ jsx(
    TextLinkButton,
    {
      weight: disabled ? "weak" : "regular",
      onClick: disabled ? noop : onClick,
      children: title
    }
  ) });
};
const convertFlagKeyToTitle = (key) => key.replace(/_/g, " ").replace(/([a-zA-Z])([A-Z0-9])/g, "$1 $2").replace(/([0-9])([a-zA-Z])/g, "$1 $2").toLowerCase().replace(/^./, (match) => match.toUpperCase());
const ToggleFeatureOverride = ({
  id,
  description,
  title,
  onOverride,
  onReset,
  overridden,
  shelf,
  value
}) => /* @__PURE__ */ jsxs(Columns, { space: "small", children: [
  /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsx(
      FieldLabel,
      {
        htmlFor: id,
        label: `${title} (${id})`,
        description
      }
    ),
    /* @__PURE__ */ jsx(
      Toggle,
      {
        togglePosition: "trailing",
        id,
        label: value ? "Enabled" : "Disabled",
        on: value === true,
        onChange: () => {
          onOverride(!value);
        }
      }
    ),
    /* @__PURE__ */ jsx(FeatureShelf, { ...shelf })
  ] }) }),
  /* @__PURE__ */ jsx(Column, { width: "content", children: overridden && /* @__PURE__ */ jsx(ResetButton, { feature: id, onClick: onReset }) })
] });
const MultiVariantFeatureOverride = ({
  id,
  onOverride,
  onReset,
  overridden,
  shelf,
  value,
  description,
  title,
  variations
}) => /* @__PURE__ */ jsxs(Columns, { space: "small", children: [
  /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsx(
      FieldLabel,
      {
        htmlFor: id,
        label: `${title} (${id})`,
        description
      }
    ),
    (variations == null ? void 0 : variations.length) ? /* @__PURE__ */ jsx(
      RadioGroup,
      {
        id,
        label: void 0,
        onChange: (event) => {
          onOverride(event.currentTarget.value);
        },
        value: (value == null ? void 0 : value.toString()) || "",
        children: variations == null ? void 0 : variations.map((variant) => /* @__PURE__ */ jsx(
          RadioItem,
          {
            label: variant,
            value: variant.toString()
          },
          variant
        ))
      }
    ) : null,
    /* @__PURE__ */ jsx(FeatureShelf, { ...shelf })
  ] }) }),
  /* @__PURE__ */ jsx(Column, { width: "content", children: overridden && /* @__PURE__ */ jsx(ResetButton, { feature: id, onClick: onReset }) })
] });
const JSONViewer = ({ data }) => /* @__PURE__ */ jsx(
  "pre",
  {
    style: {
      backgroundColor: "#f5f5f5",
      padding: "12px",
      borderRadius: "8px",
      fontFamily: "monospace",
      whiteSpace: "pre-wrap",
      overflowX: "auto",
      border: "1px solid #ddd",
      margin: "unset"
    },
    children: JSON.stringify(data, null, 2)
  }
);
const JSONFeatureOverride = ({
  id,
  onOverride,
  onReset,
  overridden,
  shelf,
  value,
  description,
  title,
  variations
}) => {
  var _a;
  return /* @__PURE__ */ jsxs(Columns, { space: "small", children: [
    /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
      /* @__PURE__ */ jsx(
        FieldLabel,
        {
          htmlFor: id,
          label: `${title} (${id})`,
          description
        }
      ),
      (variations == null ? void 0 : variations.length) ? /* @__PURE__ */ jsx(
        RadioGroup,
        {
          id,
          label: void 0,
          onChange: (event) => {
            onOverride(event.currentTarget.value);
          },
          value: (value == null ? void 0 : value.toString()) || "",
          children: variations.map((variant, index) => /* @__PURE__ */ jsx(
            RadioItem,
            {
              label: variant.label,
              value: index.toString(),
              children: /* @__PURE__ */ jsx(Table, { label: "", alignY: "center", children: /* @__PURE__ */ jsx(TableBody, { children: Object.entries(variant.value).map(([key, val]) => /* @__PURE__ */ jsxs(TableRow, { children: [
                /* @__PURE__ */ jsx(TableHeaderCell, { wrap: true, children: /* @__PURE__ */ jsx(Text, { size: "xsmall", children: convertFlagKeyToTitle(key) }) }),
                /* @__PURE__ */ jsx(TableCell, { wrap: true, children: /* @__PURE__ */ jsx(Text, { size: "xsmall", children: typeof val === "object" ? /* @__PURE__ */ jsx(JSONViewer, { data: val }) : val.toString() }) })
              ] }, key)) }) })
            },
            variant.label
          ))
        }
      ) : null,
      /* @__PURE__ */ jsx(FeatureShelf, { ...shelf, originalValue: (_a = shelf.originalValue) == null ? void 0 : _a.label })
    ] }) }),
    /* @__PURE__ */ jsx(Column, { width: "content", children: overridden && /* @__PURE__ */ jsx(ResetButton, { feature: id, onClick: onReset }) })
  ] });
};
const NoOverride = ({
  id,
  shelf
}) => /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
  /* @__PURE__ */ jsx(Text, { weight: "strong", size: "large", children: id }),
  /* @__PURE__ */ jsx(Text, { children: "This flag does not have overrides configurations, or has an unsupported type (only boolean, string and number flags are supported)." }),
  /* @__PURE__ */ jsx(FeatureShelf, { ...shelf })
] });
const FeatureOverride = ({
  feature,
  override,
  onOverride,
  onReset,
  config
}) => {
  var _a, _b;
  const {
    value: originalValue,
    variation: originalVariation,
    reason
  } = feature || {};
  const { key } = config;
  const { value: overrideValue, variation: overrideVariation } = override;
  const overridden = useMemo(
    () => overrideValue !== void 0,
    [overrideValue]
  );
  const isExperiment = isExperimentConfig(config);
  const trackingNumber = isExperimentConfig(config) ? config.trackingNumber : void 0;
  switch (override.options.type) {
    case "boolean":
      return /* @__PURE__ */ jsx(
        ToggleFeatureOverride,
        {
          title: override.options.title,
          description: override.options.description,
          id: key,
          value: Boolean(overrideValue ?? originalValue),
          overridden,
          onOverride,
          onReset,
          shelf: {
            originalValue,
            reason,
            trackingNumber,
            variation: isExperiment ? overrideVariation ?? originalVariation : void 0
          }
        }
      );
    case "string":
    case "number":
      return /* @__PURE__ */ jsx(
        MultiVariantFeatureOverride,
        {
          title: override.options.title,
          description: override.options.description,
          id: key,
          value: (_a = overrideValue ?? originalValue) == null ? void 0 : _a.toString(),
          variations: override.options.variations,
          overridden,
          onOverride,
          onReset,
          shelf: {
            originalValue: originalValue == null ? void 0 : originalValue.toString(),
            reason,
            trackingNumber,
            variation: isExperiment ? overrideVariation ?? originalVariation : void 0
          }
        }
      );
    case "json":
      return /* @__PURE__ */ jsx(
        JSONFeatureOverride,
        {
          title: override.options.title,
          description: override.options.description,
          id: key,
          value: overrideVariation ?? originalVariation,
          overridden,
          onOverride,
          onReset,
          variations: override.options.variations,
          shelf: {
            originalValue: (_b = override.options.variations) == null ? void 0 : _b[Number(originalVariation)],
            reason,
            trackingNumber,
            variation: isExperiment ? overrideVariation ?? originalVariation : void 0
          }
        }
      );
    default:
      return /* @__PURE__ */ jsx(
        NoOverride,
        {
          id: key,
          shelf: {
            trackingNumber,
            originalValue,
            reason,
            variation: isExperiment ? originalVariation : void 0
          }
        }
      );
  }
};
const Controller = ({
  section,
  features,
  config
}) => {
  const { overrides, overrideFlag } = useContext(FeatureFlags);
  return /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsx(Heading, { level: "3", children: section }),
    (config == null ? void 0 : config.length) ? config.map((c, index) => {
      const override = overrides == null ? void 0 : overrides.find((o) => o.options.key === c.key);
      if (override) {
        const feature = features == null ? void 0 : features.find((f) => f.key === c.key);
        return /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            FeatureOverride,
            {
              config: c,
              feature,
              override,
              onOverride: (val) => {
                overrideFlag == null ? void 0 : overrideFlag(c.key, val);
              },
              onReset: () => {
                overrideFlag == null ? void 0 : overrideFlag(c.key);
              }
            },
            c.key
          ),
          index < config.length - 1 && /* @__PURE__ */ jsx(Divider, {})
        ] });
      }
      return null;
    }) : /* @__PURE__ */ jsxs(Text, { align: "center", children: [
      "Your ",
      section.toLowerCase(),
      " will appear here."
    ] })
  ] });
};
const ConnectionBadge = ({ active }) => /* @__PURE__ */ jsx(
  Badge,
  {
    tone: active ? "positive" : "caution",
    title: active ? "Evaluations received from upstream" : "No evaluations received from upstream",
    children: active ? "Connected" : "Disconnected"
  }
);
const Panel = () => {
  const {
    experiments,
    experimentsConfig,
    featureFlags,
    featureFlagsConfig,
    resetAllOverrides,
    hasOverrides
  } = useContext(FeatureFlags);
  const isDisconnected = ((experimentsConfig == null ? void 0 : experimentsConfig.length) || 0) + ((featureFlagsConfig == null ? void 0 : featureFlagsConfig.length) || 0) > ((featureFlags == null ? void 0 : featureFlags.length) || 0) + ((experiments == null ? void 0 : experiments.length) || 0);
  return /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    /* @__PURE__ */ jsxs(Box, { display: "flex", justifyContent: "spaceBetween", alignItems: "center", children: [
      /* @__PURE__ */ jsx(ConnectionBadge, { active: !isDisconnected }),
      /* @__PURE__ */ jsx(ResetButton, { disabled: !hasOverrides, onClick: resetAllOverrides })
    ] }),
    /* @__PURE__ */ jsx(
      Controller,
      {
        section: "Features",
        features: featureFlags,
        config: featureFlagsConfig
      }
    ),
    /* @__PURE__ */ jsx(Divider, {}),
    /* @__PURE__ */ jsx(
      Controller,
      {
        section: "Experiments",
        features: experiments,
        config: experimentsConfig
      }
    )
  ] });
};
const ENABLE_OVERRIDE_COOKIE = "ENABLE_CANDIDATE_FEATURE_FLAGS_OVERRRIDE_CONTROLLER";
const ENABLE_OVERRIDE_QUERY_PARAM_PATTERN = "ffoc=seek";
const useIsOverrideAllowed = () => {
  const isAlreadyEnabled = useRef(
    parseDocumentCookies()[ENABLE_OVERRIDE_COOKIE] === "true"
  ).current;
  const isQueryParamEnabled = useRef(
    typeof window !== "undefined" && window.location.search.includes(ENABLE_OVERRIDE_QUERY_PARAM_PATTERN)
  ).current;
  if (!isAlreadyEnabled && isQueryParamEnabled) {
    setCookie(ENABLE_OVERRIDE_COOKIE, "true");
  }
  return isAlreadyEnabled || isQueryParamEnabled;
};
const FeatureFlagsController = () => {
  const [isOverriding, setIsOverriding] = useState(false);
  const isOverrideAllowed = useIsOverrideAllowed();
  useToggleFeatureOverride({
    isHidden: !isOverriding,
    onSuccess: () => {
      if (!isOverriding) {
        setIsOverriding(true);
      }
    }
  });
  return isOverriding && isOverrideAllowed ? /* @__PURE__ */ jsx(
    Drawer,
    {
      id: FEATURE_FLAGS_CONTAINER_SELECTOR,
      open: isOverriding,
      title: "Feature Flags Controller",
      closeLabel: "Close",
      description: /* @__PURE__ */ jsx(Text, { tone: "secondary", children: "Control which feature flags and experiments are turned on and off." }),
      onClose: () => setIsOverriding(false),
      position: "left",
      children: /* @__PURE__ */ jsx(Panel, {})
    }
  ) : null;
};
export {
  FeatureFlagCacheValueFragmentDoc,
  FeatureFlagProvider,
  FeatureFlagsController,
  extractFeatureFlagsCookies,
  parseDocumentFeatureFlagCookies,
  useFeatureFlagContext,
  useOverriddenFeatures
};
